import { useState } from 'react';
import { FileText, Copy, ExternalLink } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { SectionHeader } from './SectionHeader';
import { SummaryRow } from './SummaryRow';
import SlaCell from '@/components/OrderSla/SlaCell';
import { OrderSla } from '@/types/sla';

interface OrderInfoSectionProps {
  customId?: string;
  orderType?: string;
  trackingUrl?: string;
  trackingAuth?: string;
  clueVerificationLink?: string;
  idCheck?: boolean;
  status?: { slug: string };
  sla?: OrderSla | null;
}

export function OrderInfoSection({
  customId,
  orderType,
  trackingUrl,
  trackingAuth,
  clueVerificationLink,
  idCheck,
  sla,
}: OrderInfoSectionProps) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const handleCopy = (text?: string) => {
    if (!text) return;
    void navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
      void message.success(String(t('copied')));
    });
  };

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card p-5'>
      <SectionHeader icon={FileText} title={String(t('order_info'))} />

      <div className='divide-y divide-sd-border'>
        <SummaryRow label={String(t('order_type'))} value={orderType || String(t('order_type_normal'))} />

        <SummaryRow label={String(t('custom_id'))} value={customId ?? '—'} />

        {/* SLA is decoration — SlaCell renders a dash for every absent case. */}
        <SummaryRow label={String(t('sla_due'))}>
          <SlaCell sla={sla} showNotTracked />
        </SummaryRow>

        <SummaryRow label={String(t('tracking_url'))}>
          {trackingUrl ? (
            <div className='flex items-center gap-2'>
              <span className='max-w-[120px] truncate text-xs text-sd-muted-foreground'>
                {trackingUrl}
              </span>
              <button
                type='button'
                aria-label={copied ? String(t('copy')) : String(t('copy'))}
                onClick={() => handleCopy(trackingAuth ?? trackingUrl)}
                className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
              >
                <Copy className='h-3.5 w-3.5' />
              </button>
              <a
                href={trackingUrl}
                target='_blank'
                rel='noopener noreferrer'
                aria-label={String(t('tracking_url'))}
                className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
              >
                <ExternalLink className='h-3.5 w-3.5' />
              </a>
            </div>
          ) : undefined}
        </SummaryRow>

        {/* Bug 12: ID Check moved to PackageSection — removed from here to avoid duplication */}

        {clueVerificationLink && (
          <SummaryRow label={String(t('clue_verification'))}>
            <div className='flex items-center gap-2'>
              <span className='max-w-[120px] truncate text-xs text-sd-muted-foreground'>
                {clueVerificationLink}
              </span>
              <button
                type='button'
                aria-label={String(t('copy'))}
                onClick={() => handleCopy(clueVerificationLink)}
                className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
              >
                <Copy className='h-3.5 w-3.5' />
              </button>
              <a
                href={clueVerificationLink}
                target='_blank'
                rel='noopener noreferrer'
                className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
              >
                <ExternalLink className='h-3.5 w-3.5' />
              </a>
            </div>
          </SummaryRow>
        )}
      </div>
    </div>
  );
}
